import { Link as ScrollLink, Element, Events, scrollSpy } from "react-scroll";
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import html2pdf from "html2pdf.js";
import {
  Box,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Stack,
  useMediaQuery,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import VSNBreadcrumbs from "../../shared/VSNBreadcrumbs";
import VSNSelect from "../../shared/VSNSelect";
import VSNButton from "../../shared/VSNButton";
import useOTBStyle from "./OTBStyle";
import Completion from "./component/Completion";
import Demographics from "./component/Demographics";
import Vitals from "./component/Vitals";
import AbnormalLabs from "./component/AbnormalLabs";
import MetabolicLabs from "./component/MetabolicLabs";
import Imaging from "./component/Imaging";
import CPET from "./component/CPET";
import HumanPerformanceAssessment from "./component/HumanPerformanceAssessment";
import HPATakeaways from "./component/HPATakeaways";
import OCA from "./component/OCA";
import MRF from "./component/MRF";
import VasanaTypeComposition from "./component/VasanaTypeComposition";
import VSNLoader from "../../shared/VSNLoader";

const menuItems = [
  { name: "Completion", section: "completion", Component: Completion },
  { name: "Demographics", section: "demographics", Component: Demographics },
  { name: "Vitals", section: "vitals", Component: Vitals },
  { name: "Abnormal Labs", section: "abnormal-labs", Component: AbnormalLabs },
  {
    name: "Metabolic Labs",
    section: "metabolic-labs",
    Component: MetabolicLabs,
  },
  { name: "Imaging", section: "imaging", Component: Imaging },
  {
    name: "CPET - Cardiopulmonary Exercise Test",
    section: "cpet",
    Component: CPET,
  },
  {
    name: "Human Performance Assessment (HPA)",
    section: "human-performance-assessment",
    Component: HumanPerformanceAssessment,
  },
  { name: "HPA Takeaways", section: "hpa-takeaways", Component: HPATakeaways },
  {
    name: "OCA - Organizational Climate Assessment",
    section: "oca",
    Component: OCA,
  },
  {
    name: "Medical Recommendation Forms (MRF)",
    section: "mrf",
    Component: MRF,
  },
  {
    name: "Vasana Type Composition",
    section: "vasana-type-composition",
    Component: VasanaTypeComposition,
  },
];

const PrintOTBReport = ({ data, pdf, otbInfo }) => (
  <Box className="pdf-content">
    {menuItems.map((item, index) => (
      <Element name={item.section} key={index} className="section">
        <Box className="chart-container">
          <item.Component data={data} pdf={pdf} otbInfo={otbInfo} />
          {!data?.length && <div className="no-chart">Chart not available</div>}
        </Box>
      </Element>
    ))}
  </Box>
);

const HiddienOtb = ({
  OTBReportData,
  loaders,
  availableDates,
  selectedDate,
  OTBInfoData,
  setSelectedDate,
  triggerDownload,
  setDirectDownload,
  setTriggerDownload,
}) => {
  const isLGScreen = useMediaQuery("(max-width:1199px)");
  const isSLGScreen = useMediaQuery("(max-width:991px)");
  const printRef = useRef();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { departmentId } = useParams();

  const [activeSection, setActiveSection] = useState("completion");
  const [selectedShift, setSelectedShift] = useState("All");
  const [orignalData, setOrignalData] = useState([]);
  const classes = useOTBStyle();
  const [loadingState, setLoadingState] = useState({
    isLoading: true,
    isOTBLoading: false,
    isPdf: false,
  });

  useEffect(() => {
    Events.scrollEvent.register("begin", () => {});
    Events.scrollEvent.register("end", () => {});
    scrollSpy.update();

    return () => {
      Events.scrollEvent.remove("begin");
      Events.scrollEvent.remove("end");
    };
  }, []);

  useEffect(() => {
    if (OTBReportData) {
      setOrignalData(OTBReportData);
      setLoadingState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [OTBReportData]);

  const filterByShift = (shift) => {
    let filteredData = OTBReportData;
    if (shift !== "All") {
      filteredData = OTBReportData?.filter(
        (patient) => patient.shift === shift
      );
    }
    setOrignalData(filteredData);
  };

  const handleShiftChange = (event) => {
    const shift = event.target.value;
    setSelectedShift(shift);
    filterByShift(shift);
  };

  const resetData = () => {
    setSelectedShift("All");
    setOrignalData(OTBReportData);
  };

  // const handleClickDownloadButton = async () => {
  //   setLoadingState((prev) => ({ ...prev, isPdf: true, isOTBLoading: true }));

  //   setTimeout(async () => {
  //     try {
  //       const element = printRef.current;

  //       if (!element || element.offsetHeight === 0) {
  //         setLoadingState((prev) => ({ ...prev, isOTBLoading: false }));
  //         return;
  //       }

  //       const departmentName = OTBInfoData?.data?.name || "Department";
  //       const currentDate = new Date().toISOString().split("T")[0];
  //       const fileName = `${departmentName}_OTB_Report_${currentDate}.pdf`;

  //       const options = {
  //         margin: [5, 0, 0, 5],
  //         filename: fileName,
  //         image: { type: "jpeg", quality: 0.5 },
  //         html2canvas: { scale: 1.3, useCORS: true },
  //         jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
  //       };

  //       await html2pdf().from(element).set(options).save();
  //     } catch (error) {
  //       console.error("Error generating PDF:", error);
  //     } finally {
  //       setLoadingState((prev) => ({
  //         ...prev,
  //         isOTBLoading: false,
  //         isPdf: false,
  //       }));
  //       setDirectDownload(false);
  //     }
  //   }, 0);
  // };

  const handleClickDownloadButton = async () => {
    setLoadingState((prev) => ({ ...prev, isPdf: true, isOTBLoading: true }));

    setTimeout(async () => {
      try {
        const element = printRef.current;

        if (!element) {
          setLoadingState((prev) => ({ ...prev, isOTBLoading: false }));
          return;
        }

        await new Promise((resolve) => {
          requestAnimationFrame(() => resolve());
        });

        await new Promise((resolve) => {
          setTimeout(() => resolve(), 300);
        });

        const waitForImagesToLoad = async (container) => {
          const images = container.querySelectorAll("img");
          await Promise.all(
            Array.from(images).map(
              (img) =>
                new Promise((resolve) => {
                  if (img.complete) {
                    resolve();
                  } else {
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                  }
                })
            )
          );
        };

        await waitForImagesToLoad(element);

        if (element.offsetHeight === 0) {
          setLoadingState((prev) => ({ ...prev, isOTBLoading: false }));
          return;
        }

        const departmentName = OTBInfoData?.data?.name || "Department";
        const currentDate = new Date().toISOString().split("T")[0];
        const fileName = `${departmentName}_OTB_Report_${currentDate}.pdf`;

        const options = {
          margin: [5, 0, 0, 5],
          filename: fileName,
          image: { type: "jpeg", quality: 0.6 },
          html2canvas: { scale: 1.3, useCORS: true },
          jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
        };

        await html2pdf().from(element).set(options).save();
      } catch (error) {
        console.error("Error generating PDF:", error);
      } finally {
        setLoadingState((prev) => ({
          ...prev,
          isOTBLoading: false,
          isPdf: false,
        }));
        setDirectDownload(false);
      }
    }, 0);
  };

  useEffect(() => {
    if (triggerDownload) {
      setTimeout(() => {
        handleClickDownloadButton();
      }, 2000);
    }
    if (setTriggerDownload) {
      setTriggerDownload(false);
    }
  }, [triggerDownload]);

  const breadcrumbsRoutes = [
    { path: "/org-admin", label: "Department" },
    { path: "", label: OTBInfoData?.data?.name },
  ];

  if (loadingState.isLoading || loaders?.summaryLoader) {
    return <VSNLoader variant="fullPage" />;
  }

  return (
    <Box className={classes.otbWrapper}>
      <Drawer
        variant={isSLGScreen ? "temporary" : "permanent"}
        anchor="left"
        sx={{
          width: isLGScreen ? 250 : 320,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: isLGScreen ? 250 : 320,
            boxSizing: "border-box",
            backgroundColor: "#fff",
            marginTop: 8,
          },
        }}
      >
        <List className={classes.sidebarMenu}>
          {menuItems.map((item, index) => (
            <ScrollLink
              key={index}
              to={item.section}
              smooth
              offset={-128}
              spy
              duration={500}
              onSetActive={(section) => setActiveSection(section)}
            >
              <ListItem
                sx={{
                  backgroundColor:
                    activeSection === item.section ? "#FAF3EA" : "transparent",
                  borderRadius: 2,
                  color: activeSection === item.section ? "#8D6A3D" : "#000",
                  cursor: "pointer",
                }}
              >
                <ListItemText primary={item.name} />
              </ListItem>
            </ScrollLink>
          ))}
        </List>
      </Drawer>
      <Box sx={{ flexGrow: 1, zIndex: 1 }}>
        <Box sx={{ flexGrow: 1, zIndex: 1 }}>
          {(loadingState.isOTBLoading || loadingState.isPdf) && (
            <Box className={classes.loader}>
              <CircularProgress size={34} color="inherit" />
            </Box>
          )}
          <Box
            className={`${classes.stickyOtbHeader} ${classes.fixedOtbHeader}`}
          >
            <Stack
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
              direction={{ smd: "row" }}
            >
              <Box mr={2} mt={0.5}>
                <VSNBreadcrumbs {...{ breadcrumbsRoutes }} />
              </Box>
              <Stack
                direction="row"
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: { smd: "nowrap", xs: "wrap" },
                }}
              >
                <VSNSelect
                  onChange={(event) => setSelectedDate(event.target.value)}
                  value={
                    selectedDate ||
                    (availableDates?.length > 0 ? availableDates[0] : "")
                  }
                  options={availableDates?.map((date) => ({
                    key: date,
                    value: date,
                  }))}
                  className={classes.FilterOrganizationInput}
                />
                <VSNSelect
                  onChange={handleShiftChange}
                  value={selectedShift}
                  options={[
                    { key: "All Shifts", value: "All" },
                    { key: "A", value: "A" },
                    { key: "B", value: "B" },
                    { key: "C", value: "C" },
                    { key: "Other", value: "Other" },
                  ]}
                  className={classes.FilterOrganizationInput}
                />
                <VSNButton
                  sx={{
                    padding: "8px 10px !important",
                    background: "black",
                    "& .content": { fontSize: 16 },
                  }}
                  handleClick={resetData}
                >
                  {`Roster ${OTBReportData?.length}`}
                </VSNButton>
                <VSNButton
                  startIcon={<DownloadIcon style={{ color: "var(--white)" }} />}
                  className={classes.Button}
                  sx={{
                    padding: "8px !important",
                    fontWeight: "500 !important",
                  }}
                  disabled={loadingState.isOTBLoading || loadingState.isPdf}
                  isLoading={loadingState.isOTBLoading || loadingState.isPdf}
                  handleClick={handleClickDownloadButton}
                >
                  {loadingState.isOTBLoading || loadingState.isPdf
                    ? "Generating..."
                    : "Download Report"}
                </VSNButton>
              </Stack>
            </Stack>
          </Box>
          <div ref={printRef}>
            <PrintOTBReport
              data={orignalData}
              pdf={loadingState.isPdf}
              otbInfo={OTBInfoData}
            />
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default HiddienOtb;
